(() => {
  const configNode = document.getElementById('BardosCurrencyConfig');
  if (!configNode || window.BardosCurrency) return;

  let config;
  try { config = JSON.parse(configNode.textContent); } catch (error) { return; }

  const rate = Number(config.rate);
  if (!Number.isFinite(rate) || rate <= 0) return;

  const valid = new Set(['USD', 'CRC']);
  const baseCurrency = valid.has(config.baseCurrency) ? config.baseCurrency : 'CRC';
  const storageKey = 'bardos_display_currency';
  const filterStorageKey = 'bardos_price_filter_values_v2';
  const readFilterState = () => {
    try { return JSON.parse(sessionStorage.getItem(filterStorageKey) || '{}'); }
    catch (error) { return {}; }
  };
  const writeFilterState = state => {
    try { sessionStorage.setItem(filterStorageKey, JSON.stringify(state)); }
    catch (error) {}
  };
  const preferenceVersionKey = 'bardos_currency_preference_version';
  const preferenceVersion = 'crc-base-v1';
  let currency = localStorage.getItem(storageKey);
  if (localStorage.getItem(preferenceVersionKey) !== preferenceVersion) {
    currency = valid.has(config.defaultCurrency) ? config.defaultCurrency : 'CRC';
    localStorage.setItem(storageKey, currency);
    localStorage.setItem(preferenceVersionKey, preferenceVersion);
  } else if (!valid.has(currency)) {
    currency = valid.has(config.defaultCurrency) ? config.defaultCurrency : 'CRC';
  }

  const originals = new WeakMap();
  let rendering = false;
  let queued = false;

  const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const crcFormatter = new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const parseAmount = (value, sourceCurrency) => {
    let number = value.replace(/[^\d,.-]/g, '');
    const comma = number.lastIndexOf(',');
    const dot = number.lastIndexOf('.');

    if (comma >= 0 && dot >= 0) {
      if (comma > dot) number = number.replace(/\./g, '').replace(',', '.');
      else number = number.replace(/,/g, '');
    } else if (comma >= 0) {
      const decimals = number.length - comma - 1;
      number = decimals === 2 ? number.replace(/\./g, '').replace(',', '.') : number.replace(/,/g, '');
    } else if (dot >= 0 && sourceCurrency === 'CRC') {
      const decimals = number.length - dot - 1;
      if (decimals === 3) number = number.replace(/\./g, '');
    }

    const parsed = Number(number);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toUSD = amount => baseCurrency === 'USD' ? amount : amount / rate;
  const toCRC = amount => baseCurrency === 'CRC' ? amount : amount * rate;
  const format = (amount, target) => target === 'CRC' ? crcFormatter.format(toCRC(amount)) : usdFormatter.format(toUSD(amount));

  const moneyPattern = /(?:US\$|\$)\s*[\d.,]+(?:\s*USD)?|USD\s*[\d.,]+|₡\s*[\d.,]+(?:\s*CRC)?|CRC\s*[\d.,]+/g;
  const selector = [
    'product-price',
    '.price',
    '.price-item',
    '.compare-at-price',
    '.unit-price',
    '.cart-items__price',
    '.cart-totals',
    '.cart-drawer',
    '.bardos-best-sellers__price',
    '.product-card',
    '[data-testid*="price"]',
    '[class*="price-"]'
  ].join(',');

  const convertText = node => {
    if (node.parentElement?.closest('facet-remove-component.facets-remove__pill, facet-status-component')) {
      moneyPattern.lastIndex = 0;
      return;
    }

    const current = node.nodeValue;
    if (!current || !moneyPattern.test(current)) {
      moneyPattern.lastIndex = 0;
      return;
    }
    moneyPattern.lastIndex = 0;
    if (!originals.has(node)) originals.set(node, current);
    const original = originals.get(node);
    node.nodeValue = original.replace(moneyPattern, match => {
      const detected = /₡|CRC/.test(match) ? 'CRC' : 'USD';
      const amount = parseAmount(match, detected);
      if (amount === null) return match;
      const baseAmount = detected === baseCurrency ? amount : (detected === 'USD' ? amount * rate : amount / rate);
      return format(baseAmount, currency);
    });
    node.parentElement?.setAttribute('data-bardos-money-converted', currency);
  };

  const processPrices = root => {
    const elements = [];
    if (root instanceof Element && root.matches(selector)) elements.push(root);
    if (root.querySelectorAll) elements.push(...root.querySelectorAll(selector));
    elements.forEach(element => {
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(convertText);
    });
  };

  const updateCartMessages = () => {
    document.querySelectorAll('[data-bardos-cart-message]').forEach(element => {
      if (currency === baseCurrency) {
        element.hidden = true;
        return;
      }
      const cents = Number(element.dataset.cartTotalCents || 0);
      const baseAmount = cents / 100;
      const usd = usdFormatter.format(toUSD(baseAmount));
      const crc = crcFormatter.format(toCRC(baseAmount));
      element.textContent = String(config.cartNotice || '')
        .replace(/\[CRC\]/g, crc)
        .replace(/\[USD\]/g, usd);
      element.hidden = false;
    });
  };

  const displayFilterValue = baseAmount => {
    if (!Number.isFinite(baseAmount)) return '';
    return currency === 'CRC' ? String(Math.round(toCRC(baseAmount))) : toUSD(baseAmount).toFixed(2).replace(/\.00$/, '');
  };
  const displayToBase = displayValue => {
    const parsed = parseAmount(displayValue, currency);
    if (parsed === null) return null;
    if (currency === baseCurrency) return parsed;
    return currency === 'CRC' ? parsed / rate : parsed * rate;
  };
  const syncPriceFilter = component => {
    const originals = [component.querySelector('input[ref="minInput"]'), component.querySelector('input[ref="maxInput"]')].filter(Boolean);
    originals.forEach(original => {
      let proxy = component.querySelector('[data-bardos-price-proxy="' + original.id + '"]');
      if (!proxy) {
        proxy = document.createElement('input');
        proxy.type = 'text';
        proxy.inputMode = 'decimal';
        proxy.autocomplete = 'off';
        proxy.className = original.className + ' bardos-price-filter-display';
        proxy.id = original.id + '-display';
        proxy.setAttribute('data-bardos-price-proxy', original.id);
        proxy.setAttribute('aria-label', original.getAttribute('aria-label') || 'Precio');
        original.insertAdjacentElement('beforebegin', proxy);
        original.type = 'hidden';
        const label = component.querySelector('label[for="' + original.id + '"]');
        if (label) {
          label.htmlFor = proxy.id;
          label.setAttribute('data-bardos-price-symbol', '');
        }
        proxy.addEventListener('change', event => {
          event.stopPropagation();
          const baseValue = proxy.value.trim() === '' ? null : displayToBase(proxy.value);
          if (baseValue === null && proxy.value.trim() !== '') return;
          original.value = baseValue === null ? '' : baseValue.toFixed(2).replace(/\.00$/, '');

          const state = readFilterState();
          if (baseValue === null) {
            delete state[original.name];
          } else {
            state[original.name] = {
              currency,
              display: proxy.value.trim(),
              base: original.value
            };
          }
          writeFilterState(state);

          if (typeof component.updatePriceFilterAndResults === 'function') component.updatePriceFilterAndResults();
          window.setTimeout(() => {
            const saved = readFilterState()[original.name];
            const normalized = original.value.trim() === '' ? '' : Number(original.value);
            proxy.value = saved && saved.currency === currency && String(saved.base) === String(original.value)
              ? saved.display
              : (normalized === '' ? '' : displayFilterValue(normalized));
            updatePriceFilterLabels();
          }, 0);
        });
      }
      if (document.activeElement !== proxy) {
        const state = readFilterState();
        const saved = state[original.name];
        const baseValue = original.value.trim() === '' ? '' : Number(original.value);

        if (baseValue === '') {
          proxy.value = '';
        } else if (
          saved &&
          saved.currency === currency &&
          String(saved.base) === String(original.value)
        ) {
          proxy.value = saved.display;
        } else {
          proxy.value = !Number.isFinite(baseValue) ? '' : displayFilterValue(baseValue);
        }

        const basePlaceholder = Number(original.getAttribute('placeholder') || 0);
        proxy.placeholder = Number.isFinite(basePlaceholder) ? displayFilterValue(basePlaceholder) : '';
      }
    });
    component.querySelectorAll('[data-bardos-price-symbol]').forEach(symbol => {
      symbol.textContent = currency === 'CRC' ? '₡' : String.fromCharCode(36);
    });
    component.dataset.bardosDisplayCurrency = currency;
  };
  const updatePriceFilters = () => {
    document.querySelectorAll('price-facet-component').forEach(syncPriceFilter);
  };
  const priceLabelPattern = /(?:US\\$|\\$)\\s*[\\d\\s.,]+(?:\\s*USD)?|USD\\s*[\\d\\s.,]+|₡\\s*[\\d\\s.,]+(?:\\s*CRC)?|CRC\\s*[\\d\\s.,]+/g;

  const getPriceFilterLabel = () => {
    const params = new URL(window.location.href).searchParams;
    const minParam = params.get('filter.v.price.gte');
    const maxParam = params.get('filter.v.price.lte');
    if (minParam === null && maxParam === null) return null;

    const rangeMaxNode = document.querySelector('[ref="facetStatus"][data-range-max]');
    const rangeMaxBase = Number(rangeMaxNode?.dataset.rangeMax || 0);
    const minBase = minParam === null ? 0 : Number(minParam);
    const maxBase = maxParam === null ? rangeMaxBase : Number(maxParam);
    if (!Number.isFinite(minBase) || !Number.isFinite(maxBase)) return null;

    if (currency === 'CRC') {
      const state = readFilterState();
      const savedMin = minParam === null ? null : state['filter.v.price.gte'];
      const savedMax = maxParam === null ? null : state['filter.v.price.lte'];

      const minExact = savedMin && savedMin.currency === 'CRC'
        ? parseAmount(String(savedMin.display), 'CRC')
        : Math.round(toCRC(minBase));
      const maxExact = savedMax && savedMax.currency === 'CRC'
        ? parseAmount(String(savedMax.display), 'CRC')
        : Math.round(toCRC(maxBase));

      if (minExact === null || maxExact === null) return null;
      return crcFormatter.format(minExact) + '–' + crcFormatter.format(maxExact);
    }

    return usdFormatter.format(toUSD(minBase)) + '–' + usdFormatter.format(toUSD(maxBase));
  };

  const updatePriceFilterLabels = () => {
    const label = getPriceFilterLabel();
    if (!label) return;

    const candidates = document.querySelectorAll(
      'facet-remove-component.facets-remove__pill, facet-status-component.facets__status, [ref="facetStatus"]'
    );
    const visited = new WeakSet();

    candidates.forEach(element => {
      element.removeAttribute('data-bardos-money-converted');
      element.querySelectorAll('[data-bardos-money-converted]').forEach(child => {
        child.removeAttribute('data-bardos-money-converted');
      });

      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (visited.has(node)) continue;

        priceLabelPattern.lastIndex = 0;
        if (!priceLabelPattern.test(node.nodeValue || '')) continue;

        visited.add(node);
        if (node.nodeValue !== label) node.nodeValue = label;
        break;
      }
    });
  };

  const render = () => {
    rendering = true;
    document.documentElement.dataset.bardosCurrency = currency;
    document.querySelectorAll('[data-bardos-currency-select]').forEach(select => { select.value = currency; });
    document.querySelectorAll('[data-bardos-currency-notice]').forEach(notice => { notice.hidden = currency === baseCurrency; });
    processPrices(document);
    updatePriceFilters();
    updatePriceFilterLabels();
    requestAnimationFrame(updatePriceFilterLabels);
    updateCartMessages();
    rendering = false;
  };

  const setCurrency = value => {
    if (!valid.has(value)) return;
    currency = value;
    localStorage.setItem(storageKey, value);
    render();
    document.dispatchEvent(new CustomEvent('bardos:currency:change', { detail: { currency, rate, baseCurrency } }));
  };

  document.addEventListener('change', event => {
    if (event.target.matches('[data-bardos-currency-select]')) setCurrency(event.target.value);
  });

  const observer = new MutationObserver(mutations => {
    if (rendering || queued) return;
    const relevant = mutations.some(mutation => mutation.type === 'childList' && mutation.addedNodes.length);
    if (!relevant) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; render(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.BardosCurrency = { set: setCurrency, get: () => currency, rate, baseCurrency };

  if (
    config.customFiltersAssetUrl &&
    !customElements.get('bardos-custom-filters') &&
    !document.querySelector('script[data-bardos-custom-filters-loader]')
  ) {
    const customFiltersScript = document.createElement('script');
    customFiltersScript.src = config.customFiltersAssetUrl;
    customFiltersScript.defer = true;
    customFiltersScript.dataset.bardosCustomFiltersLoader = '';
    document.head.append(customFiltersScript);
  }

  render();
})();
